import {
  enablementFor,
  qualifiedToolName,
  slugOf,
  toolsForConnection,
  type CatalogProvider,
  type Connection,
  type ConnectionTool,
  type ServerDoc,
} from "./types";

/**
 * The dashboard's flat view of "every tool this user has", and the one piece of arithmetic
 * that decides what an allow-list becomes when a checkbox moves.
 *
 * Two pages ask those questions now -- the per-account dialog on a card, and the search
 * page that spans every account -- and getting the allow-list rule subtly different in the
 * two would be a silent bug: the difference between "expose these three" and "expose only
 * these three" is one comparison, and the wrong answer either switches tools off that the
 * user never touched or writes an allow-list that pins the account against tools it gains
 * later.
 */

/**
 * Which half of a tool list is being looked at.
 *
 * The read/write split is the only thing that divides tools without reference to the
 * account they came from, which is what makes it worth a control: "expose everything that
 * only reads" is a decision people actually take, and it is one the user would otherwise
 * have to make one checkbox at a time down a list of thirty.
 */
export type ToolKind = "all" | "read" | "write";

export const TOOL_KINDS: { id: ToolKind; label: string; hint: string }[] = [
  { id: "all", label: "All", hint: "Every tool" },
  { id: "read", label: "Read", hint: "Tools that only read — they cannot change anything" },
  { id: "write", label: "Write", hint: "Tools that can change something" },
];

/**
 * Whether one tool belongs to the half being shown.
 *
 * `readOnly` is the same flag the MCP layer publishes as `readOnlyHint`, so this filter and
 * the promise made to the model are the same claim -- a tool the user filed under "read"
 * here is one the model is told is safe to call speculatively.
 */
export function matchesKind(tool: { readOnly: boolean }, kind: ToolKind): boolean {
  return kind === "all" || (kind === "read") === tool.readOnly;
}

/** One tool on one connected account: the unit the user actually switches on and off. */
export interface AccountTool {
  /** The name the model sees, and a stable key across the whole dashboard. */
  id: string;
  conn: Connection;
  provider: CatalogProvider;
  slug: string;
  tool: ConnectionTool;
  /** The account's own switch. A tool cannot be exposed while this is off. */
  accountEnabled: boolean;
  /** Exposed to the MCP client right now: the account is on and the tool is allowed. */
  exposed: boolean;
}

/**
 * Every tool of every healthy connected account, flattened.
 *
 * Broken and revoked accounts are left out on purpose: their tools are not exposed and
 * cannot be, so listing them would offer a switch that does nothing. Reconnecting is the
 * fix, and that lives on the Providers page.
 */
export function accountTools(
  catalog: CatalogProvider[],
  connections: Connection[],
  server: (ServerDoc & { id: string }) | null
): AccountTool[] {
  const providerOf = new Map(catalog.map((p) => [p.id, p]));
  const rows: AccountTool[] = [];

  for (const conn of connections) {
    if (conn.status !== "active") continue;

    const provider = providerOf.get(conn.providerId);
    if (!provider) continue;

    const slug = slugOf(conn);
    // Per-member for a personal account, the shared team-wide state for a team one.
    const { enabled: accountEnabled, allowList } = enablementFor(conn, server);

    for (const tool of toolsForConnection(provider, conn)) {
      rows.push({
        id: qualifiedToolName(slug, tool.name),
        conn,
        provider,
        slug,
        tool,
        accountEnabled,
        exposed: accountEnabled && (!allowList || allowList.includes(tool.name)),
      });
    }
  }

  return rows.sort(
    (a, b) => a.slug.localeCompare(b.slug) || a.tool.name.localeCompare(b.tool.name)
  );
}

/**
 * What one account's allow-list becomes after switching some of its tools on or off.
 *
 * `null` in and `null` out both mean "every tool this account can offer", which is the
 * default and what most users want. Two consequences worth keeping:
 *
 *   - Unchecking the first tool has to materialise the full list and remove from it.
 *     Writing just the one name would switch everything else off.
 *   - Checking the last one collapses back to `null` rather than listing all of them,
 *     so a tool the account gains later is exposed instead of silently withheld.
 *
 * Names are re-derived from `available`, which also prunes anything stale: a Generic MCP
 * server can drop a tool, and a name in the allow-list that no longer exists makes
 * `setEnabledTools` reject every later edit as "unknown tool".
 */
export function nextAllowList(
  available: ConnectionTool[],
  allowList: string[] | null,
  names: string[],
  on: boolean
): string[] | null {
  const all = available.map((t) => t.name);
  const selected = new Set(allowList ?? all);

  for (const name of names) {
    if (on) selected.add(name);
    else selected.delete(name);
  }

  const next = all.filter((name) => selected.has(name));
  return next.length === all.length ? null : next;
}
